// Small shared pieces used across pages. Kept here rather than in App.js so the
// page modules import what they draw without pulling in the router shell.
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

export function Section({ children, alt = false, lg = false, id }) {
  return (
    <section id={id} className={`section${alt ? ' section--alt' : ''}`}>
      <div className={`wrap section__inner${lg ? ' section__inner--lg' : ''}`}>{children}</div>
    </section>
  );
}

export function Eyebrow({ children }) {
  return <span className="eyebrow">{children}</span>;
}

// A framed photo with the corner-tick motif and an optional caption.
export function Media({ src, alt, ratio = '4x3', caption, ticked = true, priority = false }) {
  return (
    <div className={`media media--${ratio}${ticked ? ' media--ticked' : ''}`}>
      <img src={src} alt={alt} loading={priority ? 'eager' : 'lazy'} decoding="async" />
      {caption && <span className="media__scrim" />}
      {caption && <span className="media__cap">{caption}</span>}
    </div>
  );
}


// Looping detection/flight footage pulled from the live site. These are the
// clips the models actually produced, so they carry the boxes themselves.
//
// Autoplay only survives in browsers when the video is muted and inline, and it
// is suppressed entirely for prefers-reduced-motion, where the poster frame is
// the whole experience. If the file fails to load the poster stays put, so a
// broken clip degrades to the still rather than a black rectangle.
//
// Below-fold clips do not get a src until they scroll near the viewport --
// without that, every <video autoplay> on the page downloads at once and the
// homepage pulls several MB before anyone has scrolled.
export function Video({ src, poster, alt, ratio = '4x3', caption, ticked = true, priority = false }) {
  const wrapRef = useRef(null);
  const vidRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const [reduced, setReduced] = useState(false);
  const [near, setNear] = useState(priority);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduced(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  useEffect(() => {
    if (priority || near) return;
    const el = wrapRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          io.disconnect();
        }
      },
      { rootMargin: '300px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [priority, near]);

  // Some browsers reject the autoplay promise even when muted; swallow it so it
  // does not surface as an unhandled rejection, and leave the poster showing.
  useEffect(() => {
    const el = vidRef.current;
    if (!el || reduced || failed || !near) return;
    const p = el.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }, [reduced, failed, near]);

  const showVideo = near && !reduced && !failed;

  return (
    <div ref={wrapRef} className={`media media--${ratio}${ticked ? ' media--ticked' : ''}`}>
      {showVideo ? (
        <video
          ref={vidRef}
          src={src}
          poster={poster}
          aria-label={alt}
          muted
          loop
          playsInline
          autoPlay
          preload={priority ? 'auto' : 'metadata'}
          onError={() => setFailed(true)}
        />
      ) : (
        <img src={poster} alt={alt} loading={priority ? 'eager' : 'lazy'} decoding="async" />
      )}
      {caption && <span className="media__scrim" />}
      {caption && <span className="media__cap">{caption}</span>}
    </div>
  );
}

export function Stat({ n, l }) {
  return (
    <div className="card card--flat">
      <div className="stat__n">{n}</div>
      <div className="stat__l">{l}</div>
    </div>
  );
}

export function Step({ n, title, children }) {
  return (
    <div className="step">
      <span className="eyebrow eyebrow--accent">{n}</span>
      <h3>{title}</h3>
      <p className="body body--sm">{children}</p>
    </div>
  );
}

export function ArrowLink({ to, children }) {
  return (
    <Link className="link-arrow" to={to}>
      {children} <span aria-hidden="true">&rarr;</span>
    </Link>
  );
}

export function RuleList({ items }) {
  return (
    <ul className="rule-list">
      {items.map((i) => (
        <li key={i}>{i}</li>
      ))}
    </ul>
  );
}
